import React, { useState, useEffect, useRef } from 'react';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend,
} from 'chart.js';
import '../Dashboard.css';
import apiClient from '../services/api';

import Sidebar        from './Sidebar';
import Analytics      from './Analytics';
import StationDetail  from './StationDetail';
import Reports        from '../pages/Reports';
import Settings       from './Settings';
import UserManagement from './UserManagement';
import TopupOBU       from './TopupOBU';
import KpiAnalytics   from './KpiAnalytics';
import VehicleAnalytics from './VehicleAnalytics';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend
);

const formatKip = (amount) => {
  if (amount === undefined || amount === null) return '₭0';
  const num = Number(amount);
  if (isNaN(num)) return '₭0';
  if (num >= 1e9) {
    return `₭${parseFloat((num / 1e9).toFixed(2))}B`;
  } else if (num >= 1e6) {
    return `₭${parseFloat((num / 1e6).toFixed(2))}M`;
  } else {
    return `₭${num.toLocaleString()}`;
  }
};

const Dashboard = ({ onLogout }) => {
  const [lang,           setLang]           = useState('lo');
  const [theme,          setTheme]          = useState('dark');
  const [currentView,    setCurrentView]    = useState('overview');
  const [currentModule,  setCurrentModule]  = useState('toll');
  const [currentStation, setCurrentStation] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const trendChartRef    = useRef(null);
  const sourceChartRef   = useRef(null);
  const locationChartRef = useRef(null);

  const [loading,            setLoading]            = useState(true);
  const [filterLoading,      setFilterLoading]      = useState(false);
  const [dateRange,          setDateRange]          = useState('all');
  const [customStart,        setCustomStart]        = useState('');
  const [customEnd,          setCustomEnd]          = useState('');
  const [sourceData,         setSourceData]         = useState([]);
  const [transactionsData,   setTransactionsData]   = useState([]);
  const [locationData,       setLocationData]       = useState([]);
  const [integrationStatus,  setIntegrationStatus]  = useState([]);
  const [kpiData,            setKpiData]            = useState(null);
  const [trendData,         setTrendData]         = useState([]);
  const [modulesMeta,       setModulesMeta]       = useState({});

  // Map filter key → days param
  const filterDaysMap = { filterToday: '1', filter7: '7', filter30: '30', filter90: '90', allSources: 'all' };

  /* ── fetch filtered data ────────────────────────── */
  const fetchDashboardData = async (filterParams = {}) => {
    try {
      const [kpiRes, trendRes, sourceRes, locRes, modulesRes] = await Promise.all([
        apiClient.dashboardGetKpi(filterParams),
        apiClient.dashboardGetTrend(filterParams),
        apiClient.dashboardGetRevenueBySource(filterParams),
        apiClient.dashboardGetLocations(filterParams),
        apiClient.dashboardGetModules(filterParams),
      ]);
      if (kpiRes?.success)        setKpiData(kpiRes.data);
      if (trendRes?.success)      setTrendData(Array.isArray(trendRes.data) ? trendRes.data : []);
      if (sourceRes?.success)     setSourceData(Array.isArray(sourceRes.data) ? sourceRes.data : []);
      if (locRes?.success)        setLocationData(Array.isArray(locRes.data) ? locRes.data : []);
      if (modulesRes?.success && Array.isArray(modulesRes.data)) {
        const map = {};
        modulesRes.data.forEach((m) => {
          map[m.key] = {
            key: m.key,
            color: m.color,
            name: { lo: m.name_la, th: m.name_en, en: m.name_en },
          };
        });
        setModulesMeta(map);
      }
    } catch (e) {
      console.warn('Filter fetch:', e.message);
    }
  };

  const handleFilterChange = async (filterKey) => {
    setDateRange(filterKey);
    setFilterLoading(true);
    const days = filterDaysMap[filterKey];
    const params = days !== 'all' ? { days } : {};
    await fetchDashboardData(params);
    setFilterLoading(false);
  };

  const handleCustomRangeApply = async () => {
    if (!customStart || !customEnd) return;
    setDateRange('customRange');
    setFilterLoading(true);
    await fetchDashboardData({ startDate: customStart, endDate: customEnd });
    setFilterLoading(false);
  };

  /* ── fetch on mount ─────────────────────────────── */
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    (async () => {
      try {
        const [kpiRes, trendRes, sourceRes, txnRes, locRes, modulesRes, apiStatusRes] = await Promise.all([
          apiClient.dashboardGetKpi(),
          apiClient.dashboardGetTrend(),
          apiClient.dashboardGetRevenueBySource(),
          apiClient.dashboardGetRecentTransactions(),
          apiClient.dashboardGetLocations(),
          apiClient.dashboardGetModules(),
          apiClient.settingsGetApiStatus(),
        ]);
        if (kpiRes?.success)    setKpiData(kpiRes.data);
        if (trendRes?.success)  setTrendData(Array.isArray(trendRes.data) ? trendRes.data : []);
        if (sourceRes?.success)    setSourceData(Array.isArray(sourceRes.data) ? sourceRes.data : []);
        if (txnRes?.success)       setTransactionsData(Array.isArray(txnRes.data) ? txnRes.data : []);
        if (locRes?.success)       setLocationData(Array.isArray(locRes.data) ? locRes.data : []);
        if (apiStatusRes?.success) setIntegrationStatus(apiStatusRes.data || []);
        if (modulesRes?.success && Array.isArray(modulesRes.data)) {
          const map = {};
          modulesRes.data.forEach((m) => {
            map[m.key] = {
              key: m.key,
              color: m.color,
              name: { lo: m.name_la, th: m.name_en, en: m.name_en },
            };
          });
          setModulesMeta(map);
        }
      } catch (e) {
        console.warn('Dashboard fetch:', e.message);
      }
      setLoading(false);
    })();
  }, []);

  /* ── i18n ───────────────────────────────────────── */
  const i18n = {
    lo: {
      navTitle: 'ກອງທຶນທາງ ສູນກາງ',
      liveLabel: 'ສົດ',
      totalToday: 'ລາຍຮັບລວມ (ວັນນີ້)',
      totalMonth: 'ລາຍຮັບ (ເດືອນນີ້)',
      growth: 'ອັດຕາການເຕີບໂຕ',
      topSource: 'ແຫຼ່ງຍອດນິຍົມ',
      revBySource: 'ລາຍຮັບຕາມປະເພດ',
      dailyTrend: 'ແນວໂນ້ມລາຍຮັບ',
      topLocations: 'ທ່ຽວທາງຊັ້ນນຳ',
      recentTx: 'ທຸລະກຳຫຼ້າສຸດ',
      allSources: 'ທຸກແຫຼ່ງ',
      alertTxt: 'WIM ມີຄວາມລ່າຊ້າ 8 ນາທີ — ກຳລັງກວດສອບ',
      filterToday: 'ວັນນີ້',
      filter7: '7 ວັນ',
      filter30: '30 ວັນ',
      filter90: '90 ວັນ',
      slDashboard: 'ໜ້າຫຼັກ',
      type: 'ປະເພດ',
      amount: 'ຈຳນວນ',
      status: 'ສະຖານະ',
      days30: 'ວັນຫຼ້າສຸດ 30 ວັນ',
      distribution: 'ສ່ວນແບ່ງ',
      liveUpdates: 'ຮ້ອງໄດ້ສົດ',
      revenueBillion: 'ລາຍຮັບ (ພັນລ້ານ ₭)',
      customRange: 'ກຳນົດເອງ',
      fromDate: 'ຈາກວັນທີ',
      toDate: 'ຫາວັນທີ',
      apply: 'ນຳໃຊ້',
    },
    th: {
      navTitle: 'กองทุนถนนส่วนกลาง',
      liveLabel: 'สด',
      totalToday: 'รายได้รวม (วันนี้)',
      totalMonth: 'รายได้ (เดือนนี้)',
      growth: 'อัตราการเติบโต',
      topSource: 'แหล่งยอดนิยม',
      revBySource: 'รายได้ตามประเภท',
      dailyTrend: 'แนวโน้มรายได้',
      topLocations: 'เส้นทางชั้นนำ',
      recentTx: 'ธุรกรรมล่าสุด',
      allSources: 'ทุกแหล่ง',
      alertTxt: 'WIM มีความล่าช้า 8 นาที — กำลังตรวจสอบ',
      filterToday: 'วันนี้',
      filter7: '7 วัน',
      filter30: '30 วัน',
      filter90: '90 วัน',
      slDashboard: 'แดชบอร์ด',
      type: 'ประเภท',
      amount: 'จำนวน',
      status: 'สถานะ',
      days30: '30 วันล่าสุด',
      distribution: 'สัดส่วน',
      liveUpdates: 'อัปเดตสด',
      revenueBillion: 'รายได้ (พันล้าน ₭)',
      customRange: 'กำหนดเอง',
      fromDate: 'จากวันที่',
      toDate: 'ถึงวันที่',
      apply: 'นำไปใช้',
    },
    en: {
      navTitle: 'Road Fund Central',
      liveLabel: 'LIVE',
      totalToday: 'Total Revenue (Today)',
      totalMonth: 'Revenue (This Month)',
      growth: 'Growth Rate',
      topSource: 'Top Source',
      revBySource: 'Revenue by Source',
      dailyTrend: 'Revenue Trend',
      topLocations: 'Top Locations',
      recentTx: 'Recent Transactions',
      allSources: 'All Sources',
      alertTxt: 'WIM system lag 8 min — investigating',
      filterToday: 'Today',
      filter7: '7 Days',
      filter30: '30 Days',
      filter90: '90 Days',
      slDashboard: 'Dashboard',
      type: 'Type',
      amount: 'Amount',
      status: 'Status',
      days30: 'Last 30 days (₭ Billion)',
      distribution: 'Distribution',
      liveUpdates: 'Live updates',
      revenueBillion: 'Revenue (₭ Billion)',
      customRange: 'Custom Range',
      fromDate: 'From Date',
      toDate: 'To Date',
      apply: 'Apply',
    },
  };
  const t = (key) => i18n[lang]?.[key] || i18n.en[key] || key;

  // Dynamic KPI label based on selected filter
  const kpiRangeLabel = {
    filterToday: { lo: '(ວັນນີ້)', th: '(วันนี้)', en: '(Today)' },
    filter7:     { lo: '(7 ວັນ)', th: '(7 วัน)', en: '(7 Days)' },
    filter30:    { lo: '(30 ວັນ)', th: '(30 วัน)', en: '(30 Days)' },
    filter90:    { lo: '(90 ວັນ)', th: '(90 วัน)', en: '(90 Days)' },
    allSources:  { lo: '(ທັງໝົດ)', th: '(ทั้งหมด)', en: '(All)' },
  };
  const rangeLabel = dateRange === 'customRange'
    ? `(${customStart || ''} ~ ${customEnd || ''})`
    : kpiRangeLabel[dateRange]?.[lang] || kpiRangeLabel.allSources[lang];
  const staticSources = [
    { key: 'bridge',  name_la: 'ຄ່າຂ້າມສະພານ', name_th: 'ค่าข้ามสะพาน', name_en: 'Bridge Fee',    percentage: 82, color: '#1890ff' },
    { key: 'topup',   name_la: 'OBU ອັດຕາເຕີມ', name_th: 'อัดเติม OBU',   name_en: 'OBU TopUp',     percentage: 18, color: '#9254de' },
  ];
  const staticLocations = [
    { name: 'ວຽງຈັນ (KM 3)', v: 42 },
    { name: 'ປາກເຊ / Pakxe', v: 31 },
    { name: 'ສະຫວັນນະເຂດ',   v: 28 },
    { name: 'ລວງພະບາງ',       v: 19 },
    { name: 'ທ່າໂຂງ',         v: 14 },
  ];
  const staticTransactions = [
    { id: 'TX-2024-18442', type: 'toll',    amount: '45,000',    plate: 'ລ 1234 ວຈ', status: 'ok',   time: '14:32' },
    { id: 'TX-2024-18441', type: 'bridge',  amount: '20,000',    plate: 'ປ 5678 ວຈ', status: 'ok',   time: '14:31' },
    { id: 'TX-2024-18440', type: 'wim',     amount: '850,000',   plate: 'ຂ 0091 ນຄ', status: 'pen',  time: '14:29' },
    { id: 'TX-2024-18439', type: 'toll',    amount: '45,000',    plate: 'ລ 3322 ວຈ', status: 'ok',   time: '14:28' },
    { id: 'TX-2024-18438', type: 'topup',   amount: '500,000',   plate: 'OBU-5678',  status: 'ok',   time: '14:27' },
    { id: 'TX-2024-18437', type: 'auction', amount: '4,500,000', plate: 'ນ 888 ວຈ',  status: 'ok',   time: '14:20' },
    { id: 'TX-2024-18436', type: 'wim',     amount: '1,200,000', plate: 'ຂ 5544 ຄຊ', status: 'fail', time: '14:15' },
  ];

  /* ── module definitions ─────────────────────────── */
  const modules = {
    toll: {
      key: 'toll', color: '#fa8c16',
      name: { lo: 'ຄ່າທາງດ່ວນ', th: 'ค่าทางด่วน', en: 'Toll Fee' },
      kpi: {
        total: { lo: 'ລາຍຮັບລວມ (ເດືອນ)', th: 'รายได้รวม (เดือน)', en: 'Total Revenue (Month)' },
        txn:   { lo: 'ທຸລະກຳທັງໝົດ',        th: 'ธุรกรรมทั้งหมด',     en: 'Total Transactions' },
        avg:   { lo: 'ລາຍຮັບ/ທຸລະກຳ',       th: 'รายได้/ธุรกรรม',     en: 'Revenue/Txn' },
        top:   { lo: 'ດ່ານຍອດນິຍົມ',         th: 'ด่านยอดนิยม',        en: 'Top Station' },
      },
      kpiVal: { total: '₭ 0B', txn: '0', avg: '₭ 0M', top: 'N/A' },
      stations: [
        { id: 'TL01', name: { lo: 'ດ່ານ KM 3 ວຽງຈັນ', th: 'ด่าน KM 3 เวียงจันทน์', en: 'KM 3 Vientiane' },   code: 'TL-VTE-01', amount: '₭ 0B', pct: 0, txn: 0, status: 'ok',  loc: 'Vientiane' },
        { id: 'TL02', name: { lo: 'ດ່ານ ທ່ານາແລງ',     th: 'ด่านท่านาแหลน',          en: 'Tha Nalaeng' },      code: 'TL-VTE-02', amount: '₭ 0B', pct: 0, txn: 0,  status: 'ok',  loc: 'Vientiane' },
        { id: 'TL03', name: { lo: 'ດ່ານ ປາກເຊ',         th: 'ด่านปากเซ',               en: 'Pakxe Gate' },       code: 'TL-CPS-01', amount: '₭ 0B', pct: 0, txn: 0,  status: 'ok',  loc: 'Champasak' },
        { id: 'TL04', name: { lo: 'ດ່ານ ສະຫວັນ 1',      th: 'ด่านสะหวัน 1',            en: 'Savannakhet 1' },    code: 'TL-SVK-01', amount: '₭ 0B', pct: 0, txn: 0,  status: 'ok',  loc: 'Savannakhet' },
        { id: 'TL05', name: { lo: 'ດ່ານ ສະຫວັນ 2',      th: 'ด่านสะหวัน 2',            en: 'Savannakhet 2' },    code: 'TL-SVK-02', amount: '₭ 0B', pct:  0, txn: 0,  status: 'ok', loc: 'Savannakhet' },
      ],
    },
    bridge: {
      key: 'bridge', color: '#1890ff',
      name: { lo: 'ຄ່າຂ້າມສະພານ', th: 'ค่าข้ามสะพาน', en: 'Bridge Fee' },
      kpi: {
        total: { lo: 'ລາຍຮັບລວມ (ເດືອນ)', th: 'รายได้รวม (เดือน)', en: 'Total Revenue (Month)' },
        txn:   { lo: 'ທຸລະກຳທັງໝົດ',        th: 'ธุรกรรมทั้งหมด',     en: 'Total Transactions' },
        avg:   { lo: 'ລາຍຮັບ/ທຸລະກຳ',       th: 'รายได้/ธุรกรรม',     en: 'Revenue/Txn' },
        top:   { lo: 'ສະພານຍອດນິຍົມ',        th: 'สะพานยอดนิยม',       en: 'Top Bridge' },
      },
      kpiVal: { total: '₭ 53.4B', txn: '237', avg: '₭ 225M', top: 'BR-VTE-01' },
      stations: [
        { id: 'BR01', name: { lo: 'ສະພານ ມິດຕະພາບ 1', th: 'สะพานมิตรภาพ 1', en: 'Friendship Bridge 1' }, code: 'BR-VTE-01', amount: '₭ 21.9B', pct: 41, txn: 97, status: 'ok',  loc: 'Vientiane' },
        { id: 'BR02', name: { lo: 'ສະພານ ມິດຕະພາບ 2', th: 'สะพานมิตรภาพ 2', en: 'Friendship Bridge 2' }, code: 'BR-SVK-01', amount: '₭ 17.6B', pct: 33, txn: 78, status: 'ok',  loc: 'Savannakhet' },
        { id: 'BR03', name: { lo: 'ສະພານ ມິດຕະພາບ 3', th: 'สะพานมิตรภาพ 3', en: 'Friendship Bridge 3' }, code: 'BR-KHM-01', amount: '₭ 9.1B', pct: 17, txn: 40, status: 'ok',  loc: 'Khammouan' },
        { id: 'BR04', name: { lo: 'ສະພານ ມິດຕະພາບ 4', th: 'สะพานมิตรภาพ 4', en: 'Friendship Bridge 4' }, code: 'BR-LPB-01', amount: '₭ 4.8B',  pct:  9, txn: 22, status: 'ok', loc: 'Luang Prabang' },
      ],
    },
    wim: {
      key: 'wim', color: '#f5222d',
      name: { lo: 'ຄ່າປັບນ້ຳໜັກ', th: 'ค่าปรับน้ำหนัก', en: 'WIM Fine' },
      kpi: {
        total: { lo: 'ລາຍຮັບລວມ (ເດືອນ)', th: 'รายได้รวม (เดือน)', en: 'Total Revenue (Month)' },
        txn:   { lo: 'ການລະເມີດທັງໝົດ',    th: 'การละเมิดทั้งหมด',   en: 'Total Violations' },
        avg:   { lo: 'ຄ່າປັບສະເລ່ຍ',        th: 'ค่าปรับเฉลี่ย',       en: 'Avg Fine' },
        top:   { lo: 'ຈຸດສູງສຸດ',            th: 'จุดสูงสุด',           en: 'Top WIM Point' },
      },
      kpiVal: { total: '₭ 0B', txn: '0', avg: '₭ 0M', top: 'N/A' },
      stations: [
        { id: 'WM01', name: { lo: 'ຈຸດ WIM KM4 ວຽງຈັນ',    th: 'จุด WIM KM4 เวียงจันทน์', en: 'WIM KM4 Vientiane' }, code: 'WM-VTE-01', amount: '₭ 0B', pct: 0, txn: 0, status: 'ok', loc: 'Vientiane' },
        { id: 'WM02', name: { lo: 'ຈຸດ WIM ດ່ານ ສາລະວັນ',  th: 'จุด WIM ด่านสาละวัน',     en: 'WIM Salavan' },       code: 'WM-SLV-01', amount: '₭ 0B',  pct: 0, txn: 0, status: 'ok',  loc: 'Salavan' },
        { id: 'WM03', name: { lo: 'ຈຸດ WIM ປາກເຊ',          th: 'จุด WIM ปากเซ',            en: 'WIM Pakxe' },         code: 'WM-CPS-01', amount: '₭ 0B',  pct: 0, txn: 0, status: 'ok',  loc: 'Champasak' },
      ],
    },
    auction: {
      key: 'auction', color: '#52c41a',
      name: { lo: 'ການປະມູນປ້າຍທະບຽນ', th: 'การประมูลป้ายทะเบียน', en: 'Plate Auction' },
      kpi: {
        total: { lo: 'ລາຍຮັບລວມ (ເດືອນ)', th: 'รายได้รวม (เดือน)', en: 'Total Revenue (Month)' },
        txn:   { lo: 'ການປະມູນ',           th: 'การประมูล',          en: 'Auctions' },
        avg:   { lo: 'ລາຄາສະເລ່ຍ',         th: 'ราคาเฉลี່ย',         en: 'Avg Price' },
        top:   { lo: 'ສູນຍອດນິຍົມ',         th: 'ศูนย์ยอดนิยม',       en: 'Top Center' },
      },
      kpiVal: { total: '₭ 0B', txn: '0', avg: '₭ 0M', top: 'N/A' },
      stations: [
        { id: 'AU01', name: { lo: 'ສູນປະມູນ ວຽງຈັນ', th: 'ศูนย์ประมูล เวียงจันทน์', en: 'Auction Center Vientiane' },   code: 'AU-VTE-01', amount: '₭ 0B', pct: 0, txn: 0, status: 'ok', loc: 'Vientiane' },
        { id: 'AU02', name: { lo: 'ສູນປະມູນ ປາກເຊ', th: 'ศูนย์ประมูล ปากเซ',         en: 'Auction Center Pakxe' },       code: 'AU-CPS-01', amount: '₭ 0B',  pct: 0, txn: 0, status: 'ok', loc: 'Champasak' },
        { id: 'AU03', name: { lo: 'ສູນປະມູນ ສະຫວັນ', th: 'ศูนย์ประมูล สะหวัน',        en: 'Auction Center Savannakhet' }, code: 'AU-SVK-01', amount: '₭ 0B',  pct: 0, txn: 0, status: 'ok', loc: 'Savannakhet' },
      ],
    },
    topup: {
      key: 'topup', color: '#9254de',
      name: { lo: 'OBU ອັດຕາເຕີມ', th: 'อัดเติม OBU', en: 'OBU TopUp' },
      kpi: {
        total: { lo: 'ລາຍຮັບລວມ (ເດືອນ)', th: 'รายได้รวม (เดือน)', en: 'Total Revenue (Month)' },
        txn:   { lo: 'ທຸລະກຳທັງໝົດ',        th: 'ธุรกรรมทั้งหมด',     en: 'Total Transactions' },
        avg:   { lo: 'ລາຍຮັບ/ທຸລະກຳ',       th: 'รายได้/ธุรກລະ',     en: 'Revenue/Txn' },
        top:   { lo: 'ສະຖານີຍອດນິຍົມ',      th: 'สถานียอดนิยม',      en: 'Top Station' },
      },
      kpiVal: { total: '₭ 11.56B', txn: '35', avg: '₭ 330M', top: 'TP-VTE-01' },
      stations: [
        { id: 'TP01', name: { lo: 'ສະຖານີ OBU ວຽງຈັນ', th: 'สถานี OBU เวียงจันทน์', en: 'OBU Station Vientiane' }, code: 'TP-VTE-01', amount: '₭ 6.0B', pct: 52, txn: 18, status: 'ok',  loc: 'Vientiane' },
        { id: 'TP02', name: { lo: 'ສະຖານີ OBU ປາກເຊ', th: 'สถานี OBU ปากเซ', en: 'OBU Station Pakxe' },     code: 'TP-CPS-01', amount: '₭ 3.4B', pct: 29, txn: 10, status: 'ok',  loc: 'Champasak' },
        { id: 'TP03', name: { lo: 'ສະຖານີ OBU ສາວັນ', th: 'สถานี OBU สะหวัน', en: 'OBU Station Savannakhet' }, code: 'TP-SVK-01', amount: '₭ 2.2B', pct: 19, txn:  7, status: 'ok',  loc: 'Savannakhet' },
      ],
    },
  };

  /* ── handlers ───────────────────────────────────── */
  const handleLangChange = (l) => setLang(l);
  const handleThemeChange = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    document.documentElement.setAttribute('data-theme', next);
  };
  const handleLogout = () => {
    localStorage.removeItem('rf_token');
    setShowProfileMenu(false);
    if (onLogout) {
      onLogout();
    } else {
      window.location.reload();
    }
  };
  const handleNavClick = (page) => {
    const moduleKeys = ['bridge', 'wim', 'toll', 'auction', 'topup'];
    if (moduleKeys.includes(page)) {
      setCurrentView('drilldown');
      setCurrentModule(page);
      setCurrentStation(null);
    } else {
      setCurrentView(page);
      setCurrentStation(null);
    }
  };

  /* ── chart data ─────────────────────────────────── */
  // Trend series is loaded from backend; no local hardcoded values.

  const gridColor    = theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const tickColor    = theme === 'dark' ? '#8892a4' : '#546e7a';
  const tooltipBase  = { backgroundColor: 'rgba(255,255,255,0.96)', titleColor: '#2c3e50', bodyColor: '#546e7a', borderColor: '#e1e4e8', borderWidth: 1, padding: 14 };

  const srcLabel  = (s) => {
    if (!s) return '';
    if (lang === 'lo') return s.name_la || s.name_en || s.module || s.key;
    if (lang === 'th') return s.name_en || s.name_la || s.module || s.key;
    return s.name_en || s.name_la || s.module || s.key;
  };

  const displaySources = Array.isArray(sourceData) ? sourceData : [];

  // Backend returns `amount` in LAK; charts expect Billion values.
  const displayLocations = Array.isArray(locationData) ? locationData.map((l) => ({
    name: lang === 'lo' ? l.name_la : l.name_en,
    v: typeof l.amount === 'number' ? l.amount / 1000000000 : 0,
  })) : [];

  const formatTxId = (id) => {
    if (!id) return '';
    if (id.startsWith('TX-')) {
      const parts = id.split('-');
      if (parts.length >= 5) {
        return `TX-${parts.slice(2).join('-')}`;
      }
    }
    if (id.length > 12) {
      return id.substring(0, 12) + '...';
    }
    return id;
  };

  const displayTxns = Array.isArray(transactionsData) ? transactionsData.map((tx) => {
    const mappedStatus =
      tx.status === 'pending'
        ? 'pen'
        : tx.status === 'failed'
          ? 'fail'
          : tx.status === 'voided'
            ? 'fail'
            : tx.status === 'ok'
              ? 'ok'
              : tx.status;
    return { ...tx, status: mappedStatus };
  }) : [];

  const srcColors = { toll: '#fa8c16', bridge: '#1890ff', wim: '#f5222d', auction: '#52c41a', topup: '#9254de' };

  const chartTrendData = trendData && trendData.length > 0
    ? {
        labels: trendData.map((d) => {
          if (!d.date) return '';
          // Today view: already formatted as "HH:00"
          if (dateRange === 'filterToday') return d.date;
          // Multi-day: show day number or MM/DD
          const parts = d.date.split('-');
          return parts.length === 3 ? `${parts[1]}/${parts[2]}` : d.date;
        }).filter(Boolean),
        datasets: [
          {
            data: trendData.map((d) => d.amount),
            borderColor: '#1890ff',
            backgroundColor: 'rgba(24,144,255,0.08)',
            fill: true,
            tension: 0.4,
            pointRadius: trendData.length > 30 ? 0 : 2,
            borderWidth: 2,
          },
        ],
      }
    : { labels: [], datasets: [{ data: [] }] };
  const chartSourceData = {
    labels: displaySources.map(srcLabel),
    datasets: [
      {
        data: displaySources.map((s) => s.percentage),
        backgroundColor: displaySources.map((s) => srcColors[s.module] || srcColors[s.key] || '#888'),
        borderWidth: 0,
      },
    ],
  };
  const chartLocationData = {
    labels: displayLocations.map(l => l.name),
    datasets: [{ data: displayLocations.map(l => l.v), backgroundColor: 'rgba(24,144,255,0.7)', borderRadius: 4 }],
  };

  const chartOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: tooltipBase },
    scales: {
      x: { grid: { color: gridColor }, ticks: { color: tickColor, font: { size: 10 } } },
      y: { grid: { color: gridColor }, ticks: { color: tickColor, font: { size: 11 }, callback: v => `₭${v}B` } },
    },
  };
  const doughnutOptions = {
    responsive: true, maintainAspectRatio: false, cutout: '72%',
    plugins: { legend: { display: false }, tooltip: { ...tooltipBase, callbacks: { label: v => `  ${v.label}: ${v.raw}%` } } },
  };
  const barOptions = {
    responsive: true, maintainAspectRatio: false, indexAxis: 'y',
    plugins: { legend: { display: false }, tooltip: tooltipBase },
    scales: {
      x: { grid: { color: gridColor }, ticks: { color: tickColor, font: { size: 11 }, padding: 10 } },
      y: { grid: { display: false }, ticks: { color: tickColor, font: { size: 12 }, padding: 10 } },
    },
  };

  const statusBadge = {
    ok:   { bg: 'rgba(82,196,26,.15)',  color: '#52c41a', text: 'OK'   },
    pen:  { bg: 'rgba(250,173,20,.15)', color: '#faad14', text: 'PEND' },
    fail: { bg: 'rgba(245,34,45,.15)',  color: '#f5222d', text: 'FAIL' },
  };

  const overviewSeries = Array.isArray(trendData) ? trendData : [];
  const last9Trend = overviewSeries.slice(-9);

  // KPI totals from kpiData (filtered by selected range)
  const kpiTotals = kpiData && typeof kpiData === 'object' ? kpiData : {};
  const overallTotalB = Object.values(kpiTotals).reduce((sum, m) => sum + (Number(m?.total) || 0), 0) / 1e9;
  // For "today" filter show today's total; otherwise show range total
  const overallMonthB = overallTotalB;
  const lastTrendAmt = overviewSeries.length ? Number(overviewSeries[overviewSeries.length - 1]?.amount) || 0 : 0;
  const overallTodayB = dateRange === 'filterToday'
    ? overallTotalB
    : lastTrendAmt > 0 ? lastTrendAmt : overallMonthB / Math.max(1, overviewSeries.length);

  // Growth based on average of last 7 points vs previous 7 points.
  const growthCalc = (() => {
    if (overviewSeries.length < 14) return { pct: 0, up: true };
    const last7 = overviewSeries.slice(-7).map((d) => Number(d.amount) || 0);
    const prev7 = overviewSeries.slice(-14, -7).map((d) => Number(d.amount) || 0);
    const lastAvg = last7.reduce((a, v) => a + v, 0) / last7.length;
    const prevAvg = prev7.reduce((a, v) => a + v, 0) / prev7.length;
    if (prevAvg <= 0) return { pct: 0, up: true };
    const pct = ((lastAvg - prevAvg) / prevAvg) * 100;
    return { pct, up: pct >= 0 };
  })();

  const computedKpis = (() => {
    const sparkMax = Math.max(1, ...last9Trend.map((d) => Number(d.amount) || 0));
    const sparkBars = last9Trend.map((d) => {
      const v = Number(d.amount) || 0;
      const scaled = Math.round((v / sparkMax) * 7 + 4);
      return Math.min(12, Math.max(3, scaled));
    });
    // Pad spark bars if not enough data
    while (sparkBars.length < 9) sparkBars.push(4);

    const entries = Object.entries(kpiTotals);
    const sortedByRevenue = entries.sort((a, b) => (b[1]?.total || 0) - (a[1]?.total || 0));
    const topModule = sortedByRevenue.length ? sortedByRevenue[0][0] : null;
    const topModuleMeta = topModule ? modulesMeta[topModule] : null;
    const totalAll = entries.reduce((s, [, v]) => s + (v?.total || 0), 0);
    const topSharePct = sortedByRevenue.length && totalAll > 0
      ? Math.round(((sortedByRevenue[0][1]?.total || 0) / totalAll) * 100)
      : 0;

    const topSourceName = topModuleMeta
      ? (lang === 'lo' ? topModuleMeta.name?.lo : topModuleMeta.name?.en) || topModule
      : topModule || '-';

    // Total transactions across all modules
    const totalTxn = entries.reduce((s, [, v]) => s + (v?.transactions || 0), 0);

    return [
      {
        label: 'totalToday',
        labelSuffix: rangeLabel,
        val: formatKip(overallTodayB * 1e9),
        sub: `${growthCalc.up ? '+' : ''}${growthCalc.pct.toFixed(1)}%`,
        color: '#1890ff',
        bars: sparkBars,
        up: growthCalc.up,
      },
      {
        label: 'totalMonth',
        labelSuffix: rangeLabel,
        val: formatKip(overallMonthB * 1e9),
        sub: `${totalTxn.toLocaleString()} txn`,
        color: '#52c41a',
        bars: sparkBars,
        up: true,
      },
      {
        label: 'growth',
        labelSuffix: '',
        val: `${growthCalc.up ? '+' : ''}${growthCalc.pct.toFixed(1)}%`,
        sub: 'vs previous 7d',
        color: '#9254de',
        bars: sparkBars,
        up: growthCalc.up,
      },
      {
        label: 'topSource',
        labelSuffix: '',
        val: topSourceName === '-' ? '-' : topSourceName,
        sub: `${topSharePct}% of total`,
        color: '#fa8c16',
        bars: sparkBars,
        up: topSharePct >= 0,
      },
    ];
  })();

  /* ── overview render ────────────────────────────── */
  const renderOverview = () => (
    <>
      <div className="section-header">
        <div>
          <div className="section-title">{t('slDashboard')}</div>
          <div className="section-sub">{new Date().toLocaleString()}</div>
        </div>
        <div className="date-range">📅 {new Date().toLocaleDateString()}</div>
      </div>

      <div className="filter-row" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        {['filterToday', 'filter7', 'filter30', 'filter90', 'allSources'].map((k) => (
          <div
            key={k}
            className={`filter-chip ${dateRange === k ? 'active' : ''}`}
            onClick={() => handleFilterChange(k)}
            style={{ cursor: 'pointer', opacity: filterLoading && dateRange !== k ? 0.5 : 1 }}
          >
            {filterLoading && dateRange === k ? '...' : t(k)}
          </div>
        ))}
        <div style={{ width: '1px', height: '20px', background: 'var(--border)', margin: '0 8px' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text3)' }}>{t('fromDate')}:</span>
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              style={{
                background: 'var(--bg3)',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                padding: '4px 8px',
                fontSize: '12px',
                color: 'var(--text)',
                fontFamily: 'var(--font)',
                outline: 'none',
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text3)' }}>{t('toDate')}:</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              style={{
                background: 'var(--bg3)',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                padding: '4px 8px',
                fontSize: '12px',
                color: 'var(--text)',
                fontFamily: 'var(--font)',
                outline: 'none',
              }}
            />
          </div>
          <button
            onClick={handleCustomRangeApply}
            disabled={!customStart || !customEnd || filterLoading}
            style={{
              background: 'var(--blue)',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              padding: '5px 12px',
              fontSize: '12px',
              cursor: (!customStart || !customEnd || filterLoading) ? 'not-allowed' : 'pointer',
              opacity: (!customStart || !customEnd || filterLoading) ? 0.5 : 1,
              fontFamily: 'var(--font)',
              fontWeight: '600',
              transition: 'all 0.2s ease',
            }}
          >
            {filterLoading && dateRange === 'customRange' ? '...' : t('apply')}
          </button>
        </div>
      </div>

      <div className="kpi-grid">
        {computedKpis.map((k) => (
          <div key={k.label} className="kpi" style={{ position: 'relative', overflow: 'hidden' }}>
            <div className="kpi-accent" style={{ background: k.color }} />
            <div className="kpi-label">
              {t(k.label)}{k.labelSuffix ? <span style={{ fontSize: '10px', color: 'var(--text3)', marginLeft: '4px' }}>{k.labelSuffix}</span> : null}
            </div>
            <div className="kpi-value" style={{ color: k.color }}>{k.val}</div>
            <div className={`kpi-sub ${k.up ? 'kpi-up' : 'kpi-down'}`}>{k.up ? '↑' : '↓'} {k.sub}</div>
            <div className="kpi-spark" style={{ color: k.color }}>
              {k.bars.map((h, i) => <div key={i} className="spark-bar" style={{ height: `${h * 3}px` }} />)}
            </div>
            {filterLoading && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--r)' }}>
                <div style={{ width: '16px', height: '16px', border: `2px solid ${k.color}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Module Revenue Row ───────────────────────────────────────────── */}
      {(() => {
        const MODULE_DEF = [
          { key: 'bridge',  icon: '◈', color: '#1890ff', bg: 'rgba(24,144,255,.12)', label: { lo: 'BRIDGE REVENUE',  th: 'BRIDGE REVENUE',  en: 'BRIDGE REVENUE'  }, sub: { lo: 'ຄ່າຂ້າມສະພານ', th: 'ค่าข้ามสะพาน',   en: 'Bridge Fee'     } },
          { key: 'wim',     icon: '⬡', color: '#f5222d', bg: 'rgba(245,34,45,.12)',  label: { lo: 'WIM VIOLATIONS',  th: 'WIM VIOLATIONS',  en: 'WIM VIOLATIONS'  }, sub: { lo: 'ຄ່າປັບນ້ຳໜັກ', th: 'ค่าปรับน้ำหนัก', en: 'WIM Fine'       } },
          { key: 'toll',    icon: '◆', color: '#fa8c16', bg: 'rgba(250,140,22,.12)', label: { lo: 'TOLL REVENUE',    th: 'TOLL REVENUE',    en: 'TOLL REVENUE'    }, sub: { lo: 'ຄ່າທາງດ່ວນ',    th: 'ค่าทางด่วน',      en: 'Toll Fee'       } },
          { key: 'auction', icon: '◆', color: '#52c41a', bg: 'rgba(82,196,26,.12)',  label: { lo: 'AUCTION PLATE',   th: 'AUCTION PLATE',   en: 'AUCTION PLATE'   }, sub: { lo: 'ປະມູນປ້າຍ',    th: 'ປະມູນປ້າຍ',      en: 'Plate Auction'  } },
        ];
        return (
          <div className="kpi-grid" style={{ marginTop: '0' }}>
            {MODULE_DEF.map(m => {
              const v = kpiData?.[m.key] || {};
              const total = v.total || 0;
              const txn   = v.transactions || 0;
              const avg   = v.average || 0;
              const allTotal = Object.values(kpiData || {}).reduce((s, x) => s + (x?.total || 0), 0);
              const sharePct = allTotal > 0 ? ((total / allTotal) * 100).toFixed(1) : '0';
              const isWim = m.key === 'wim';
              return (
                <div key={m.key} className="kpi" style={{ position: 'relative', overflow: 'hidden', cursor: 'pointer' }}
                  onClick={() => handleNavClick(m.key)}>
                  <div className="kpi-accent" style={{ background: m.color }} />
                  <div className="kpi-label" style={{ fontSize: '10px', letterSpacing: '.06em', color: m.color, fontWeight: '700' }}>
                    {m.label[lang] || m.label.en}
                  </div>
                  <div className="kpi-value" style={{ color: m.color }}>
                    {isWim
                      ? txn.toLocaleString()
                      : formatKip(total)}
                  </div>
                  <div className="kpi-sub" style={{ color: 'var(--text3)' }}>
                    {isWim
                      ? `${formatKip(total)} revenue`
                      : `${txn.toLocaleString()} txn`}
                  </div>
                  <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ flex: 1, height: '3px', borderRadius: '2px', background: 'var(--bg4)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: '2px', background: m.color, width: `${sharePct}%`, transition: 'width 0.8s ease' }} />
                    </div>
                    <span style={{ fontSize: '10px', color: m.color, fontWeight: '700', flexShrink: 0 }}>
                      {sharePct}%
                    </span>
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text3)', marginTop: '4px' }}>
                    {m.sub[lang] || m.sub.en} · avg ₭{avg.toLocaleString()}
                  </div>
                  {filterLoading && (
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--r)' }}>
                      <div style={{ width: '14px', height: '14px', border: `2px solid ${m.color}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      <div className="chart-row">
        <div className="card">
          <div className="card-title">{t('dailyTrend')}</div>
          <div className="card-sub">
            {dateRange === 'filterToday' ? (lang === 'lo' ? 'ລາຍຮັບຕໍ່ຊົ່ວໂມງ (ວັນນີ້)' : lang === 'th' ? 'รายได้ต่อชั่วโมง (วันนี้)' : 'Hourly Revenue (Today)')
              : dateRange === 'filter7' ? (lang === 'lo' ? '7 ວັນຫຼ້າສຸດ' : lang === 'th' ? '7 วันล่าสุด' : 'Last 7 Days')
              : dateRange === 'filter30' ? (lang === 'lo' ? '30 ວັນຫຼ້າສຸດ' : lang === 'th' ? '30 วันล่าสุด' : 'Last 30 Days')
              : dateRange === 'filter90' ? (lang === 'lo' ? '90 ວັນຫຼ້າສຸດ' : lang === 'th' ? '90 วันล่าสุด' : 'Last 90 Days')
              : t('days30')}
          </div>
          <div className="chart-wrap" style={{ height: '200px' }}>
            <Line ref={trendChartRef} data={chartTrendData} options={chartOptions} />
          </div>
        </div>
        <div className="card">
          <div className="card-title">{t('revBySource')}</div>
          <div className="card-sub">{t('distribution')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'center' }}>
            <div className="chart-wrap" style={{ height: '160px' }}>
              <Doughnut ref={sourceChartRef} data={chartSourceData} options={doughnutOptions} />
            </div>
            <div className="mod-list">
              {displaySources.map(s => (
                <div key={s.key || s.module} className="mod-item" onClick={() => handleNavClick(s.key || s.module)}>
                  <div className="mod-dot" style={{ background: s.color || srcColors[s.key || s.module] }} />
                  <div className="mod-name">{srcLabel(s)}</div>
                  <div className="mod-val">{s.percentage}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="chart-row">
        <div className="card">
          <div className="card-title">{t('topLocations')}</div>
          <div className="card-sub">{t('revenueBillion')}</div>
          <div className="chart-wrap" style={{ height: '180px' }}>
            <Bar ref={locationChartRef} data={chartLocationData} options={barOptions} />
          </div>
        </div>
        <div className="card">
          <div className="card-title">{t('recentTx')}</div>
          <div className="card-sub">{t('liveUpdates')}</div>
          <table className="mini-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>{t('type')}</th>
                <th>{t('amount')}</th>
                <th>{t('status')}</th>
              </tr>
            </thead>
            <tbody>
              {displayTxns.slice(0, 7).map(tx => (
                <tr key={tx.id}>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--text3)' }} title={tx.id}>
                    {formatTxId(tx.id)}
                  </td>
                  <td>
                    <span style={{ padding: '2px 6px', borderRadius: '3px', fontSize: '10px', background: 'var(--bg4)', color: 'var(--text2)' }}>
                      {tx.type}
                    </span>
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--text2)' }}>
                    ₭ {tx.amount}
                  </td>
                  <td>
                    <span style={{ padding: '2px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: '600', background: statusBadge[tx.status]?.bg, color: statusBadge[tx.status]?.color }}>
                      {statusBadge[tx.status]?.text}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  /* ── settings tab router ─────────────────────────── */
  const settingsTab = () => {
    if (currentView === 'settings-audit') return 'audit';
    if (currentView === 'settings-api')   return 'api';
    return 'general';
  };

  /* ── main render ─────────────────────────────────── */
  const renderContent = () => {
    switch (currentView) {
      case 'analytics':
      case 'analytics-revenue':
        return <Analytics lang={lang} theme={theme} />;
      case 'analytics-topup':
        return <TopupOBU lang={lang} theme={theme} dateRange={dateRange} customStart={customStart} customEnd={customEnd} />;
      case 'analytics-kpi':
        return <KpiAnalytics lang={lang} theme={theme} />;
      case 'analytics-vehicle':
        return <VehicleAnalytics lang={lang} theme={theme} />;
      case 'reports':
      case 'reports-monthly':
      case 'export':
        return <Reports lang={lang} theme={theme} />;
      case 'users':
        return <UserManagement lang={lang} theme={theme} />;
      case 'settings':
      case 'settings-audit':
      case 'settings-api':
        return <Settings lang={lang} theme={theme} initialTab={settingsTab()} />;
      case 'drilldown':
        if (currentModule === 'topup') {
          return <TopupOBU lang={lang} theme={theme} dateRange={dateRange} customStart={customStart} customEnd={customEnd} />;
        }
        return (
          <StationDetail
            lang={lang}
            theme={theme}
            module={
              modulesMeta[currentModule] || {
                key: currentModule,
                color: undefined,
                name: { lo: currentModule, en: currentModule, th: currentModule },
              }
            }
            station={currentStation
              ? { id: currentStation }
              : null}
            onBack={() => setCurrentStation(null)}
            onNavigateStation={id => setCurrentStation(id)}
            dateRange={dateRange}
            customStart={customStart}
            customEnd={customEnd}
          />
        );
      default:
        return renderOverview();
    }
  };

  return (
    <div className="shell" data-theme={theme}>

      {/* ── TOP NAV ── */}
      <nav className="topnav">
        <div className="logo">
          <div className="logo-mark">RF</div>
          <span>{t('navTitle')}</span>
        </div>

        {currentView === 'drilldown' && (
          <div className="breadcrumb">
            <span className="bc-link" onClick={() => handleNavClick('overview')}>Dashboard</span>
            <span className="bc-sep">/</span>
            <span className="bc-cur">
              {lang === 'lo'
                ? modulesMeta[currentModule]?.name?.lo
                : modulesMeta[currentModule]?.name?.en}
              {modulesMeta[currentModule] ? '' : currentModule}
            </span>
            {currentStation && (
              <>
                <span className="bc-sep">/</span>
                <span className="bc-cur">
                  {currentStation}
                </span>
              </>
            )}
          </div>
        )}

        <div className="live-dot" />
        <span className="live-label">{t('liveLabel')}</span>

        <div className="nav-right">
          {['lo', 'th', 'en'].map(l => (
            <button key={l} className={`lang-btn ${lang === l ? 'active' : ''}`} onClick={() => handleLangChange(l)}>
              {l === 'lo' ? 'ລາວ' : l === 'th' ? 'ไทย' : 'EN'}
            </button>
          ))}
          <div style={{ width: '1px', height: '20px', background: 'var(--border)', margin: '0 4px' }} />
          <button className="theme-btn" onClick={handleThemeChange} title="Toggle theme">
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
          <div style={{ width: '1px', height: '20px', background: 'var(--border)', margin: '0 4px' }} />
          <div style={{ position: 'relative' }}>
            <button
              style={{
                width: '28px', height: '28px', borderRadius: '50%',
                background: 'var(--blue3)', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: '11px', fontWeight: '600', color: 'var(--blue)',
                border: 'none', cursor: 'pointer', transition: 'all 0.2s ease',
              }}
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              title="User menu"
            >
              AD
            </button>
            {showProfileMenu && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: '8px',
                background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                minWidth: '150px', zIndex: 1000,
              }}>
                <div style={{ padding: '8px 0' }}>
                  <div style={{
                    padding: '8px 16px', fontSize: '12px', color: 'var(--text3)',
                    borderBottom: '1px solid var(--border)',
                  }}>
                    Admin User
                  </div>
                  <button
                    onClick={handleLogout}
                    style={{
                      width: '100%', textAlign: 'left', padding: '8px 16px',
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      fontSize: '13px', color: 'var(--text2)', transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => e.target.style.background = 'var(--bg3)'}
                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                  >
                    🚪 Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* ── SIDEBAR ── */}
      <Sidebar
        lang={lang}
        theme={theme}
        currentView={currentView}
        currentModule={currentModule}
        onNavigate={handleNavClick}
        integrationStatus={integrationStatus}
      />

      {/* ── MAIN CONTENT ── */}
      <main className="main">
        {renderContent()}
      </main>

    </div>
  );
};

export default Dashboard;
